// Boris-styled form primitives (register.css .rg-field / .rg-label / .rg-input /
// .rg-field-row / .rg-select): label-above, white rounded inputs, side-by-side
// rows, and a 3-box date of birth. Used to align the reg forms to Boris's design
// without restyling the legacy shared FormField (which many screens still use).
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { Spacing } from '@/shared/theme';

const LINE = 'rgba(28,31,33,0.12)';
const ERR = 'rgba(200,60,46,0.55)';

// Boris `.rg-field-row` stays two columns on every breakpoint — First/Last, the
// three DOB boxes, and the shipping rows keep side-by-side down to ~320px rather
// than stacking on mobile.
export const RgFieldRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

export const RgField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

export const RgLabel = styled.label`
  font-weight: 500;
  font-size: 15px;
  color: ${BorisScreener.ink};
  letter-spacing: 0.005em;
`;

export const RgLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: ${BorisScreener.btnPrimary};
  text-decoration: underline;
  text-underline-offset: 2px;
`;

export const RgHint = styled.span`
  font-size: 13px;
  color: ${BorisScreener.inkFaint};
`;

export const RgErrorHint = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${BorisScreener.bad};
`;

// Boris `.rg-screen`: a flex column that fills the main column's height, split
// into an `.rg-body` scroller + a pinned `.rg-foot` sibling. Register.css: the
// ONLY scroller is `.rg-body`; `.rg-foot` sits OUTSIDE it as a true flex
// sibling, never scrolling. Steps that own their own screen (rather than
// relying on Layout's fallback scroller) apply these flex properties to their
// own root element; see MedicareCoverageStep/ShippingStep.
export const RgScreen = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

// Boris `.rg-body-wrap`/`.rg-list-wrap`: `position:relative` sibling host for
// the scroller + its bottom fade overlay (register.css: `position:relative;
// flex:1; min-height:0; display:flex`).
const RgBodyWrap = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
`;

const RgBodyScroller = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 26px;
  /* Fields fill this box edge-to-edge, so an input's focus ring (box-shadow,
     drawn outside the border-box) has zero room before overflow-x:hidden clips
     it. This padding is the ring's clearance; FlowMain's max-width is widened
     by the same amount (Layout.tsx) so the visible field width is unchanged. */
  padding: 0 ${Spacing.x0_5}px;

  &::-webkit-scrollbar {
    width: 0;
  }
  scrollbar-width: none;
`;

// Boris `.rg-list-fade`: an absolutely-positioned bottom gradient, toggled via
// a `.show` class, cueing "there's more below" (register.css:403-408).
const RgListFade = styled.div<{ $show: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 64px;
  pointer-events: none;
  background: linear-gradient(to top, ${BorisScreener.sand} 0%, rgba(245, 242, 238, 0) 100%);
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: opacity 0.35s ease;
`;

// Boris `.rg-body`: the ONLY scroller (register.css `.rg-body { flex:1;
// min-height:0; overflow-y:auto; overflow-x:hidden }`). Holds the 620px-capped
// `.rg-inner` content: here that's just the gap-26px content column, since
// the 620px cap already comes from Layout's FlowMain. Also holds, for steps
// that render their own phase bar, that too (kept out of Layout's fallback
// scroller so it doesn't freeze in place once a step owns its own RgBody).
//
// Wraps children in Boris's `.rg-body-wrap` + `.rg-list-fade` pair so every
// consumer gets the bottom-scroll fade cue and a hidden native scrollbar for
// free, matching register.css exactly without each step re-deriving it.
export const RgBody: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState(true);

  const updateFade = (el: HTMLDivElement) => {
    setFade(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  };

  useEffect(() => {
    if (ref.current) updateFade(ref.current);
  }, []);

  return (
    <RgBodyWrap>
      <RgBodyScroller ref={ref} onScroll={(e) => updateFade(e.currentTarget)}>
        {children}
      </RgBodyScroller>
      <RgListFade $show={fade} aria-hidden="true" />
    </RgBodyWrap>
  );
};

// Boris `.rg-foot`: a `flex-shrink:0` sibling OUTSIDE the scroller (register.css
// `.reg-dt-main .rg-foot { padding:18px 56px 38px; max-width:620px; margin:0
// auto }`), replacing the old `position:sticky` model: true flexbox pinning
// makes the old bugs (content bleeding through the fade; a stray gap below the
// CTA) structurally impossible rather than just visually patched. The negative
// bottom margin cancels the ambient 32px bottom padding Layout's FlowMain still
// provides for steps not yet migrated onto this split, so this footer's own
// padding becomes the ONLY bottom spacing. No horizontal padding of its own:
// relies on that same ambient side padding for the 56px inset, matching Boris's
// rendered result without double-applying it.
export const RgFoot = styled.div`
  flex-shrink: 0;
  width: 100%;
  max-width: 620px;
  margin: 0 auto;
  margin-bottom: -32px;
  padding: 20px 0 32px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: linear-gradient(0deg, ${BorisScreener.sand} 70%, rgba(245, 242, 238, 0) 100%);
`;

export const inputCss = (hasError?: boolean) => `
  width: 100%;
  height: 60px;
  border-radius: 15px;
  border: 1.5px solid ${hasError ? ERR : LINE};
  background: #fff;
  padding: 0 18px;
  font-family: inherit;
  font-weight: 500;
  font-size: 19px;
  color: ${BorisScreener.ink};
  outline: none;
  transition: border-color 0.16s ease, box-shadow 0.16s ease;
`;

const StyledInput = styled.input<{ $error?: boolean; $mono?: boolean; $widthCh?: number; $hasReveal?: boolean }>`
  ${({ $error }) => inputCss($error)}
  ${({ $mono }) => ($mono ? "font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 18px; letter-spacing: 0.06em;" : '')}
  ${({ $widthCh }) => ($widthCh ? 'max-width: ' + $widthCh + 'ch;' : '')}
  /* Leave room for the password reveal toggle so the value never runs under it. */
  ${({ $hasReveal }) => ($hasReveal ? 'padding-right: 48px;' : '')}
  &::placeholder {
    color: ${BorisScreener.inkFaint};
    font-weight: 400;
  }
  &:focus {
    border-color: ${BorisScreener.ink};
    box-shadow: 0 0 0 3px rgba(28, 31, 33, 0.07);
  }
`;

const SelectWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledSelect = styled.select<{ $error?: boolean; $placeholder?: boolean }>`
  ${({ $error }) => inputCss($error)}
  appearance: none;
  cursor: pointer;
  padding-right: 44px;
  color: ${({ $placeholder }) => ($placeholder ? BorisScreener.inkFaint : BorisScreener.ink)};
  font-weight: ${({ $placeholder }) => ($placeholder ? 400 : 500)};
  &:focus {
    border-color: ${BorisScreener.ink};
    box-shadow: 0 0 0 3px rgba(28, 31, 33, 0.07);
  }
`;

const Chevron = styled.span`
  position: absolute;
  right: 16px;
  pointer-events: none;
  display: flex;
  color: ${BorisScreener.inkSoft};
`;

// Password reveal toggle (Boris `.rg-input` has no reveal affordance of its own;
// this preserves the legacy show/hide password behaviour on the migrated field).
const PasswordWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const RevealButton = styled.button`
  position: absolute;
  right: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${BorisScreener.inkSoft};
`;

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M10.7 6.2A9.9 9.9 0 0 1 12 6c6.4 0 10 7 10 7a17.3 17.3 0 0 1-3.1 3.8M6.3 7.6A17.2 17.2 0 0 0 2 12s3.6 7 10 7a9.7 9.7 0 0 0 4-.9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.9 10.1a3 3 0 0 0 4.2 4.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Text field (label above + white rounded input) ──
type RgTextFieldProps = {
  name: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
  mono?: boolean;
  error?: string | false;
  hint?: string;
  rightLabel?: React.ReactNode;
  /** Boris `.rg-input.mbi` is width-capped (18.5ch); pass to narrow the input. */
  widthCh?: number;
  /**
   * Accessible labels for the password reveal toggle. Only rendered when
   * `type === 'password'`; defaults are English so non-password callers (and
   * their tests) are unaffected. Pass localized strings for the password field.
   */
  revealLabels?: { show: string; hide: string };
};

export const RgTextField: React.FC<RgTextFieldProps> = ({
  name, label, value, onChange, onBlur, onFocus, placeholder, type = 'text', inputMode,
  autoComplete, mono, error, hint, rightLabel, widthCh,
  revealLabels = { show: 'Show password', hide: 'Hide password' },
}) => {
  const isPassword = type === 'password';
  const [revealed, setRevealed] = React.useState(false);
  const effectiveType = isPassword ? (revealed ? 'text' : 'password') : type;

  const input = (
    <StyledInput
      id={name}
      name={name}
      data-testid={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      placeholder={placeholder}
      type={effectiveType}
      inputMode={inputMode}
      autoComplete={autoComplete}
      aria-invalid={!!error}
      $error={!!error}
      $mono={mono}
      $widthCh={widthCh}
      $hasReveal={isPassword}
    />
  );

  return (
    <RgField>
      <LabelRow>
        <RgLabel htmlFor={name}>{label}</RgLabel>
        {rightLabel}
      </LabelRow>
      {isPassword ? (
        <PasswordWrap>
          {input}
          <RevealButton
            type="button"
            data-testid={`${name}-reveal`}
            aria-label={revealed ? revealLabels.hide : revealLabels.show}
            aria-pressed={revealed}
            onClick={() => setRevealed((v) => !v)}
          >
            {revealed ? <EyeOffIcon /> : <EyeIcon />}
          </RevealButton>
        </PasswordWrap>
      ) : (
        input
      )}
      {error ? <RgErrorHint role="alert">{error}</RgErrorHint> : hint ? <RgHint>{hint}</RgHint> : null}
    </RgField>
  );
};

// ── Select field (native, Boris styling + chevron) ──
type RgSelectFieldProps = {
  name: string;
  label: string;
  value: string;
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void;
  error?: string | false;
};

export const RgSelectField: React.FC<RgSelectFieldProps> = ({
  name, label, value, placeholder, options, onChange, onBlur, error,
}) => (
  <RgField>
    <RgLabel htmlFor={name}>{label}</RgLabel>
    <SelectWrap>
      <StyledSelect
        id={name}
        name={name}
        data-testid={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        aria-invalid={!!error}
        $error={!!error}
        $placeholder={!value}
      >
        <option value="" disabled hidden>{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </StyledSelect>
      <Chevron aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Chevron>
    </SelectWrap>
    {error && <RgErrorHint role="alert">{error}</RgErrorHint>}
  </RgField>
);

// ── Date of birth: three boxes (MM / DD / YYYY) with auto-advance ──
const DobBox = styled(StyledInput)`
  text-align: left;
`;

type RgDobProps = {
  label: string;
  // Assembled "MM/DD/YYYY" (partials keep the slashes so the caller's length check works).
  value: string;
  onChange: (assembled: string) => void;
  onBlur?: () => void;
  error?: string | false;
};

export const RgDob: React.FC<RgDobProps> = ({ label, value, onChange, onBlur, error }) => {
  const [mm = '', dd = '', yyyy = ''] = value ? value.split('/') : [];
  const ddRef = React.useRef<HTMLInputElement>(null);
  const yyRef = React.useRef<HTMLInputElement>(null);

  const emit = (m: string, d: string, y: string) => {
    // Keep MM/DD/YYYY shape so the shared dob validator (length === 10) works once full.
    onChange(`${m}/${d}/${y}`);
  };
  const onMM = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
    emit(v, dd, yyyy);
    if (v.length === 2) ddRef.current?.focus();
  };
  const onDD = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
    emit(mm, v, yyyy);
    if (v.length === 2) yyRef.current?.focus();
  };
  const onYY = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    emit(mm, dd, v);
  };

  return (
    <RgField>
      <RgLabel>{label}</RgLabel>
      <RgFieldRow style={{ flexDirection: 'row' }}>
        <DobBox name="dob-mm" data-testid="dob-mm" inputMode="numeric" maxLength={2} value={mm} onChange={onMM} onBlur={onBlur} placeholder="MM" $error={!!error} aria-label="Month" />
        <DobBox ref={ddRef} name="dob-dd" data-testid="dob-dd" inputMode="numeric" maxLength={2} value={dd} onChange={onDD} onBlur={onBlur} placeholder="DD" $error={!!error} aria-label="Day" />
        <DobBox ref={yyRef} name="dob-yyyy" data-testid="dob-yyyy" inputMode="numeric" maxLength={4} value={yyyy} onChange={onYY} onBlur={onBlur} placeholder="YYYY" $error={!!error} aria-label="Year" />
      </RgFieldRow>
      {error && <RgErrorHint role="alert">{error}</RgErrorHint>}
    </RgField>
  );
};
